import React from 'react';
import type { TextObject } from './types';
import { useTokens } from './theme';

type Segment =
  | { kind: 'text'; value: string }
  | { kind: 'bold'; value: string }
  | { kind: 'italic'; value: string }
  | { kind: 'strike'; value: string }
  | { kind: 'code'; value: string }
  | { kind: 'link'; url: string; label: string }
  | { kind: 'mention'; label: string };

function parse(text: string): Segment[] {
  const segments: Segment[] = [];
  let i = 0;
  while (i < text.length) {
    if (text[i] === '<') {
      const end = text.indexOf('>', i);
      if (end !== -1) {
        const inner = text.slice(i + 1, end);
        if (inner.startsWith('!')) {
          segments.push({ kind: 'mention', label: `@${inner.slice(1).split('|')[0]}` });
        } else if (inner.startsWith('@')) {
          segments.push({ kind: 'mention', label: inner });
        } else if (inner.startsWith('#')) {
          const parts = inner.slice(1).split('|');
          segments.push({ kind: 'mention', label: `#${parts[1] ?? parts[0]}` });
        } else {
          const [url, label] = inner.split('|');
          segments.push({ kind: 'link', url, label: label ?? url });
        }
        i = end + 1;
        continue;
      }
    }
    if (text[i] === '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        segments.push({ kind: 'bold', value: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (text[i] === '_') {
      const end = text.indexOf('_', i + 1);
      if (end !== -1) {
        segments.push({ kind: 'italic', value: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (text[i] === '~') {
      const end = text.indexOf('~', i + 1);
      if (end !== -1) {
        segments.push({ kind: 'strike', value: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (text[i] === '`' && text[i + 1] !== '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        segments.push({ kind: 'code', value: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    const last = segments[segments.length - 1];
    if (last?.kind === 'text') last.value += text[i];
    else segments.push({ kind: 'text', value: text[i] });
    i++;
  }
  return segments;
}

export function Mrkdwn({ text, style }: { text: string; style?: React.CSSProperties }) {
  const t = useTokens();

  const codeStyle: React.CSSProperties = {
    fontFamily: 'Monaco, Menlo, Consolas, monospace',
    fontSize: '0.875em',
    background: t.codeBg,
    border: `1px solid ${t.codeBorder}`,
    borderRadius: 3,
    padding: '0 3px',
  };

  const parts = text.split(/(```[\s\S]*?```)/g);

  const rendered = parts.map((part, pi) => {
    if (part.startsWith('```') && part.endsWith('```')) {
      return (
        <pre
          key={pi}
          style={{
            background: t.codeBg,
            border: `1px solid ${t.codeBorder}`,
            borderRadius: 4,
            padding: '8px 12px',
            fontFamily: 'Monaco, Menlo, Consolas, monospace',
            fontSize: 13,
            overflowX: 'auto',
            margin: '4px 0',
            color: t.text,
          }}
        >
          <code>{part.slice(3, -3).replace(/^\n/, '')}</code>
        </pre>
      );
    }
    return part.split('\n').map((line, li, arr) => {
      const segs = parse(line);
      const nodes = segs.map((seg, si) => {
        if (seg.kind === 'text') return <React.Fragment key={si}>{seg.value}</React.Fragment>;
        if (seg.kind === 'bold') return <b key={si}>{seg.value}</b>;
        if (seg.kind === 'italic') return <i key={si}>{seg.value}</i>;
        if (seg.kind === 'strike') return <s key={si}>{seg.value}</s>;
        if (seg.kind === 'code')
          return (
            <code key={si} style={codeStyle}>
              {seg.value}
            </code>
          );
        if (seg.kind === 'link')
          return (
            <a
              key={si}
              href={seg.url}
              style={{ color: t.link, textDecoration: 'none' }}
              target="_blank"
              rel="noreferrer"
            >
              {seg.label}
            </a>
          );
        if (seg.kind === 'mention')
          return (
            <span
              key={si}
              style={{
                background: t.mentionBg,
                color: t.mentionText,
                borderRadius: 3,
                padding: '0 2px',
                fontWeight: 500,
              }}
            >
              {seg.label}
            </span>
          );
        return null;
      });
      return (
        <React.Fragment key={`${pi}-${li}`}>
          {nodes}
          {li < arr.length - 1 && <br />}
        </React.Fragment>
      );
    });
  });

  return <span style={style}>{rendered}</span>;
}

export function PlainText({ text, style }: { text: string; style?: React.CSSProperties }) {
  return <span style={style}>{text}</span>;
}

export function Text({ obj, style }: { obj: TextObject; style?: React.CSSProperties }) {
  if (obj.type === 'mrkdwn') return <Mrkdwn text={obj.text} style={style} />;
  return <PlainText text={obj.text} style={style} />;
}

export function renderMrkdwn(text: string): string {
  return text
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/~(.*?)~/g, '<s>$1</s>')
    .replace(/<([^|>]+)\|([^>]+)>/g, '<a href="$1">$2</a>')
    .replace(/<([^>]+)>/g, '<a href="$1">$1</a>');
}
